import apiClient from "./api-client.ts";

/**
 * A factory function to create standardized service methods for API calls
 * 
 * @param baseUrl The base URL for the service
 * @returns An object with methods for making API calls
 */
export function createService(baseUrl: string) {
  return {
    /**
     * Make a GET request to fetch a resource by ID
     * 
     * @param id The ID of the resource
     * @param config Additional request configuration
     * @returns The resource data
     */
    getById: <T>(id: string, config?: any) => {
      return apiClient.get<T>(`${baseUrl}/${id}`, config);
    },

    /**
     * Make a GET request to fetch all resources
     * 
     * @param params Query parameters
     * @param config Additional request configuration
     * @returns The resources data
     */
    getAll: <T>(params?: any, config?: any) => {
      return apiClient.get<T>(baseUrl, { ...config, params });
    },

    /**
     * Make a POST request to create a new resource
     * 
     * @param data The data for the new resource
     * @param config Additional request configuration
     * @returns The created resource data
     */
    create: <T>(data: any, config?: any) => {
      return apiClient.post<T>(baseUrl, data, config);
    },

    /**
     * Make a PUT request to update a resource
     * 
     * @param id The ID of the resource
     * @param data The updated data for the resource
     * @param config Additional request configuration
     * @returns The updated resource data
     */
    update: <T>(id: string, data: any, config?: any) => {
      return apiClient.put<T>(`${baseUrl}/${id}`, data, config);
    },

    /**
     * Make a PATCH request to partially update a resource
     * 
     * @param id The ID of the resource
     * @param data The partial data for the resource
     * @param config Additional request configuration
     * @returns The updated resource data
     */
    patch: <T>(id: string, data: any, config?: any) => {
      return apiClient.patch<T>(`${baseUrl}/${id}`, data, config);
    },

    /**
     * Make a DELETE request to delete a resource
     * 
     * @param id The ID of the resource
     * @param config Additional request configuration
     * @returns The deleted resource data or a success message
     */
    delete: <T>(id: string, config?: any) => {
      return apiClient.delete<T>(`${baseUrl}/${id}`, config);
    },

    /**
     * Make a custom GET request
     * 
     * @param path The path to append to the base URL
     * @param config Additional request configuration
     * @returns The response data
     */
    customGet: <T>(path: string, config?: any) => {
      return apiClient.get<T>(`${baseUrl}${path}`, config);
    },

    /**
     * Make a custom POST request
     * 
     * @param path The path to append to the base URL
     * @param data The data to send
     * @param config Additional request configuration
     * @returns The response data
     */
    customPost: <T>(path: string, data?: any, config?: any) => {
      return apiClient.post<T>(`${baseUrl}${path}`, data, config);
    },

    /**
     * Make a custom PUT request
     * 
     * @param path The path to append to the base URL
     * @param data The data to send
     * @param config Additional request configuration
     * @returns The response data
     */
    customPut: <T>(path: string, data?: any, config?: any) => {
      return apiClient.put<T>(`${baseUrl}${path}`, data, config);
    },

    /**
     * Make a custom DELETE request
     * 
     * @param path The path to append to the base URL
     * @param config Additional request configuration
     * @returns The response data
     */
    customDelete: <T>(path: string, config?: any) => {
      return apiClient.delete<T>(`${baseUrl}${path}`, config);
    },

    /**
     * Make a custom PATHC request
     *
     * @param path The path to append to the base URL
     * @param config Additional request configuration
     * @returns The response data
     */
    customPatch: <T>(path: string, config?: any) => {
      return apiClient.patch<T>(`${baseUrl}${path}`, config);
    },


  };
}